import { nanoid } from 'nanoid';
import { WbsAccountResponse } from 'src/api/FiretrolApiClient';
import { WbsTreeData } from 'src/routes/Jobs/WBS/components/types';

export { getErrorMessage } from './getErrorMessage';
export { FIELD_LIMITS, characterCount } from './fieldLimits';
export {
  getValidationErrors,
  toFormikFieldErrors,
  applyValidationErrors,
} from './getValidationErrors';
export type { ApplyValidationErrorsResult } from './getValidationErrors';
export { logger } from './logger';
export { showToast, showErrorToast, showSuccessToast } from './showToast';
export { resolveWorkflowButtonColor } from './workflowButtonColor';

// CTC-connected WBS accounts don't have a costCode row (costCode is null); their
// display number instead lives in effectiveCostCode (set server-side from
// externalWbsId for CTC accounts, or costCode.code otherwise). Accept a loosely
// typed param since callers pass WbsAccountResponse, WbsAccountDto, and WbsTreeData,
// only some of which currently carry effectiveCostCode.
export const getAccountCostCode = (
  account?: {
    effectiveCostCode?: string | null;
    costCode?: { code?: string | null } | null;
  } | null,
): string => account?.effectiveCostCode ?? account?.costCode?.code ?? '';

// Generic tree traversal function that can be customized with different logic
export function traverseWbsTree<T>(
  data: WbsTreeData[],
  visitor: (account: WbsTreeData, parentPath: string) => T | null,
  parentPath = '',
): T[] {
  const results: T[] = [];

  data.forEach((item: WbsTreeData) => {
    const currentPath = `${parentPath}${item.name} / `;
    const result = visitor(item, currentPath);

    if (result !== null) {
      results.push(result);
    }

    // Recursively traverse children
    if (item.children && item.children.length > 0) {
      results.push(...traverseWbsTree(item.children, visitor, currentPath));
    }
  });

  return results;
}

// Helper function to find a node in the tree by ID
export function findNodeInTree(
  nodes: WbsTreeData[],
  targetId: string,
): WbsTreeData | null {
  for (const node of nodes) {
    if (node.id === targetId) {
      return node;
    }
    if (node.children && node.children.length > 0) {
      const found = findNodeInTree(node.children, targetId);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

// Helper function to update a node in the tree by ID
export function updateNodeInTree(
  nodes: WbsTreeData[],
  targetId: string,
  updates: Partial<WbsTreeData>,
): WbsTreeData[] {
  return nodes.map((node) => {
    if (node.id === targetId) {
      return { ...node, ...updates };
    }
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: updateNodeInTree(node.children, targetId, updates),
      };
    }
    return node;
  });
}

// Helper function to remove a node from the tree by ID
export function removeNodeFromTree(
  nodes: WbsTreeData[],
  targetId: string,
): WbsTreeData[] {
  return nodes.filter((node) => {
    if (node.id === targetId) {
      return false;
    }
    if (node.children && node.children.length > 0) {
      node.children = removeNodeFromTree(node.children, targetId);
    }
    return true;
  });
}

// Helper function to add a node to the tree at a specific parent
export function addNodeToTree(
  nodes: WbsTreeData[],
  parentId: string | null,
  newNode: WbsTreeData,
): WbsTreeData[] {
  if (parentId === null) {
    return [...nodes, newNode];
  }

  return nodes.map((node) => {
    if (node.id === parentId) {
      return {
        ...node,
        children: [...(node.children || []), newNode],
      };
    }
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: addNodeToTree(node.children, parentId, newNode),
      };
    }
    return node;
  });
}

// Helper function to sort tree data recursively by code and name
export const sortTreeData = (nodes: WbsTreeData[]): WbsTreeData[] => {
  return nodes
    .map((node) => ({
      ...node,
      children:
        node.children && node.children.length > 0
          ? sortTreeData(node.children)
          : [],
    }))
    .sort((a, b) => {
      // Sort by cost code name, then by account name
      if (a.costCode?.name && b.costCode?.name) {
        return a.costCode.name.localeCompare(b.costCode.name);
      } else if (a.name && b.name) {
        return a.name.localeCompare(b.name);
      } else {
        return 0;
      }
    });
};

export const convertAccountsToTreeData = (
  accounts?: WbsAccountResponse[],
  filterFn?: (account: WbsTreeData) => boolean,
  parentPath = '',
): WbsTreeData[] => {
  if (!accounts) return [];

  // First, convert WbsAccountResponse[] to WbsTreeData[]
  const convertedAccounts: WbsTreeData[] = accounts.map((account) => ({
    id: account.id,
    wbsId: account.wbsId || '',
    parentId: account.parentId || null,
    costCodeId: account.costCodeId || null,
    name: account.name || '',
    description: account.description || '',
    accountType: (account.accountType as 'labor' | 'cost' | 'other') || 'other',
    initialQuantityEstimate: account.initialQuantityEstimate ?? null,
    currentQuantityEstimate: account.currentQuantityEstimate ?? null,
    currentQuantityActual: account.currentQuantityActual ?? null,
    initialHoursEstimate: account.initialHoursEstimate || 0,
    currentHoursEstimate: account.currentHoursEstimate || 0,
    currentHoursActual: account.currentHoursActual || 0,
    // Canonicalize: server emits "HEADS"/"FEET" for grouping-derived leaves; normalize to lowercase
    unitOfMeasure:
      (account.unitOfMeasure?.toLowerCase() as
        | '$'
        | 'hours'
        | 'pieces'
        | 'heads'
        | 'feet'
        | ''
        | null) || '$',
    apply: account.apply ?? true,
    active: account.active ?? true,
    archived: account.archived ?? false,
    timesheetsOnly: account.timesheetsOnly ?? false,
    progressManaged: account.progressManaged ?? false,
    perDiemEligible: account.perDiemEligible ?? true,
    scalePayEnabled: account.scalePayEnabled ?? false,
    isCtcConnected: account.isCtcConnected ?? false,
    externalWbsId: account.externalWbsId || null,
    startDate: account.startDate || null,
    finishDate: account.finishDate || null,
    createdAt: account.createdAt || new Date(),
    updatedAt: account.updatedAt || new Date(),
    isLeaf: account.isLeaf ?? true,
    costCode: account.costCode
      ? {
          id: account.costCode.id,
          code: account.costCode.code,
          name: account.costCode.name,
          createdAt: new Date(),
          updatedAt: new Date(),
        }
      : undefined,
    effectiveCostCode: account.effectiveCostCode,
    // Use inverseParent as children (EF Core navigation property)
    children:
      account.inverseParent && account.inverseParent.length > 0
        ? convertAccountsToTreeData(account.inverseParent, filterFn, parentPath)
        : [],
  }));

  const treeData = convertedAccounts
    .filter(filterFn || (() => true)) // Apply filter if provided, otherwise include all
    .map((account) => {
      const isFolder = !account.isLeaf;

      const costCodeName = account.costCode?.name || '';

      // For leaf accounts (charge codes), use cost code name instead of account name
      const displayName =
        account.isLeaf && account.apply
          ? costCodeName || account.name || ''
          : account.name;

      const currentPath = parentPath
        ? `${parentPath} / ${displayName}`
        : displayName;

      // The account's display code (number), distinct from costCodeName above.
      // CTC-connected accounts have costCode=null; effectiveCostCode carries
      // their number (from externalWbsId) in that case.
      const code = getAccountCostCode(account);

      return {
        ...account,
        name: displayName,
        code,
        path: currentPath,
        children: isFolder
          ? convertAccountsToTreeData(
              accounts.find((a) => a.id === account.id)?.inverseParent || [],
              filterFn,
              currentPath,
            )
          : [],
        isFolder: isFolder,
      };
    });

  // Sort the tree data by code and name
  return sortTreeData(treeData);
};

const rollupValues = <K extends keyof WbsTreeData>(
  node: WbsTreeData,
  field: K,
  options: { skipProgressManaged?: boolean } = {},
) => {
  // If the node has no children, return its value
  if (!node.children || node.children.length === 0) {
    const value = node[field];
    return typeof value === 'number' ? value : 0;
  }

  // Recursively calculate the sum of values of the children. When summing
  // domain-unit fields (quantity in heads/feet/etc.), progress-managed
  // children are skipped because their units are not comparable with siblings.
  // Hours are uniform across labor accounts and roll up normally.
  const childrenValueSum = node.children.reduce((sum, child) => {
    if (options.skipProgressManaged && child.progressManaged) return sum;
    return sum + rollupValues(child, field, options);
  }, 0);

  // Update the node's value to include its children's value sum
  const currentValue = node[field];
  const numericValue = typeof currentValue === 'number' ? currentValue : 0;
  node[field] = numericValue + childrenValueSum;

  // Return the updated value
  return node[field] as number;
};

export const hasProgressManagedDescendant = (node: WbsTreeData): boolean => {
  if (!node.children?.length) return false;
  return node.children.some(
    (c) => c.progressManaged || hasProgressManagedDescendant(c),
  );
};

export const buildWBSTree = (items: WbsTreeData[], addLevel = false) => {
  const rootItems: WbsTreeData[] = [];
  const lookup: Record<string, WbsTreeData> = {};
  // Create a lookup map
  items.forEach((item) => {
    lookup[item.id] = {
      ...item,
      children: [],
    };
  });

  // Assign children to their respective parents
  items.forEach((item) => {
    if (item.parentId === null) {
      rootItems.push(lookup[item.id]);
    } else {
      if (item.isLeaf) {
        const target = {
          ...lookup[item.id],
        };
        lookup[item.parentId]?.children.push(target);
      } else {
        const target = {
          ...lookup[item.id],
          currentQuantityEstimate: 0,
          currentQuantityActual: 0,
          currentHoursEstimate: 0,
          currentHoursActual: 0,
        };
        lookup[item.parentId]?.children.push(target);
      }
    }
  });
  const after = rootItems.map((item) => {
    return {
      ...item,
      currentQuantityEstimate: rollupValues(item, 'currentQuantityEstimate', {
        skipProgressManaged: true,
      }),
      currentQuantityActual: rollupValues(item, 'currentQuantityActual', {
        skipProgressManaged: true,
      }),
      currentHoursEstimate: rollupValues(item, 'currentHoursEstimate'),
      currentHoursActual: rollupValues(item, 'currentHoursActual'),
    };
  });

  if (after.length === 0) return after;
  if (addLevel) {
    return [
      {
        id: nanoid(),
        wbsId: '',
        parentId: null,
        costCodeId: null,
        code: '',
        name: 'WBS',
        description: '',
        accountType: null,
        initialQuantityEstimate: 0,
        currentQuantityEstimate: after[0].currentQuantityEstimate || 0,
        currentQuantityActual: after[0].currentQuantityActual || 0,
        initialHoursEstimate: 0,
        currentHoursEstimate: after[0].currentHoursEstimate || 0,
        currentHoursActual: after[0].currentHoursActual || 0,
        unitOfMeasure: '$',
        apply: false,
        active: true,
        archived: false,
        timesheetsOnly: false,
        externalWbsId: null,
        isCtcConnected: false,
        createdAt: new Date(),
        updatedAt: new Date(),
        isLeaf: false,
        startDate: null,
        finishDate: null,
        children: after,
        isFolder: after.length > 0,
      } as WbsTreeData,
    ];
  }
  return after;
};

export const flattenTree = (node: WbsTreeData) => {
  // Start with the current node in the flattened array
  let flattened = [{ ...node, children: undefined }]; // Remove children from the node

  // If the node has children, recursively flatten them and concatenate to the result
  if (node.children && node.children.length > 0) {
    node.children.forEach((child) => {
      flattened = flattened.concat(flattenTree(child));
    });
  }

  return flattened;
};

/**
 * Abbreviates a string to the specified length, adding '...' if truncated
 * @param str - The string to abbreviate
 * @param maxLength - The maximum length before truncation
 * @returns The abbreviated string
 */
export const abbreviateString = (str: string, maxLength: number): string => {
  if (!str || str.length <= maxLength) {
    return str;
  }
  return str.substring(0, maxLength) + '...';
};

export * from './format';

/**
 * Triggers a file download by creating a temporary anchor element
 * @param url - The URL to download from
 * @param filename - The filename to save as
 */
export const triggerDownload = (url: string, filename: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
};
